import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import {
  IconDefinition,
  faAsterisk,
  faBan,
  faChartLine,
  faChartSimple,
  faClockRotateLeft,
  faDiagramProject,
  faEdit,
  faEnvelopeCircleCheck,
  faEye,
  faFileCode,
  faFolder,
  faIdCard,
  faPassport,
  faPlay,
  faSitemap,
  faSquarePlus,
  faTags,
  faTimes,
  faTrash,
  faUserLock,
} from '@fortawesome/free-solid-svg-icons';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { SingleValue } from 'react-select';
import {
  Alert,
  ArrayInput,
  Card,
  ResourcePicker,
  Select,
  Value,
  ValueList,
} from '.';
import {
  Permission,
  PermissionAction,
  PermissionMode,
  PermissionResourceType,
} from '../types';
import { FlowIdsPicker } from './FlowIdsPicker';
import { SelectOptionProps } from './Select';

type TokenPermissionEditorProps = {
  index: number;
  permission: Permission;
  pending?: boolean;
  onChange?: (permission: Permission) => void;
};

export function TokenPermissionEditor({
  index,
  permission,
  pending,
  onChange,
}: TokenPermissionEditorProps): ReactElement {
  const [value, setValue] = useState<Permission>({
    mode: 'allow',
    action: '*',
  });

  useEffect(() => {
    setValue(permission);
  }, [permission]);

  const [resourceTypeEnabled, setResourceTypeEnabled] = useState<boolean>(true);
  const [listIdsEnabled, setListIdsEnabled] = useState<boolean>(true);
  const [itemIdsEnabled, setItemIdsEnabled] = useState<boolean>(true);
  const [indexIdsEnabled, setIndexIdsEnabled] = useState<boolean>(true);
  const [identityIdsEnabled, setIdentityIdsEnabled] = useState<boolean>(true);
  const [groupsEnabled, setGroupsEnabled] = useState<boolean>(true);
  const [flowIdsEnabled, setFlowIdsEnabled] = useState<boolean>(true);

  useEffect(() => {
    let resourceTypeEnabled = true;
    let listIdsEnabled = false;
    let itemIdsEnabled = false;
    let indexIdsEnabled = false;
    let identityIdsEnabled = false;
    let groupsEnabled = false;
    let flowIdsEnabled = false;

    // All/None and sync schema permissions don't apply to a resource type
    if (value.action === '*' || value.action === 'sync-schema') {
      resourceTypeEnabled = false;
    }

    // Create list permission
    // (do nothing, this matches the default values)

    // Other list permissions
    if (
      ['view', 'update', 'delete'].includes(value.action) &&
      value.resourceType === 'list'
    ) {
      listIdsEnabled = true;
    }

    // Create item permission
    if (value.action === 'create' && value.resourceType === 'item') {
      listIdsEnabled = true;
    }

    // Other item permissions
    if (
      ['view', 'update', 'delete', 'restore'].includes(value.action) &&
      value.resourceType === 'item'
    ) {
      listIdsEnabled = true;
      itemIdsEnabled = true;
    }

    // Create index permission
    if (value.action === 'create' && value.resourceType === 'index') {
      listIdsEnabled = true;
    }

    // Other index permissions
    if (
      ['view', 'update', 'delete'].includes(value.action) &&
      value.resourceType === 'index'
    ) {
      listIdsEnabled = true;
      indexIdsEnabled = true;
    }

    // Create identity permission
    // (do nothing, this matches the default values)

    // Other identity permissions
    if (
      ['view', 'update', 'delete'].includes(value.action) &&
      value.resourceType === 'identity'
    ) {
      identityIdsEnabled = true;
    }

    // Register / authenticate / reset password / verify email / self identity
    // permission
    if (
      [
        'register',
        'authenticate',
        'reset-password',
        'verify-email',
        'update',
        'delete',
      ].includes(value.action) &&
      value.resourceType === 'identity'
    ) {
      groupsEnabled = true;
    }

    // Create item with identity permission
    if (
      value.action === 'create-with-identity' &&
      value.resourceType === 'item'
    ) {
      listIdsEnabled = true;
    }

    // Other item with identity permissions
    if (
      [
        'view-with-identity',
        'update-with-identity',
        'delete-with-identity',
        'restore-with-identity',
      ].includes(value.action) &&
      value.resourceType === 'item'
    ) {
      listIdsEnabled = true;
      itemIdsEnabled = true;
    }

    // View item events with identity permission
    if (
      value.action === 'view-with-identity' &&
      value.resourceType === 'event'
    ) {
      listIdsEnabled = true;
      itemIdsEnabled = true;
    }

    // View item stats with identity permission
    if (
      value.action === 'view-with-identity' &&
      value.resourceType === 'stats'
    ) {
      listIdsEnabled = true;
      itemIdsEnabled = true;
    }

    // View list/item/index events permission
    if (value.action === 'view' && value.resourceType === 'event') {
      listIdsEnabled = true;
      itemIdsEnabled = true;
      indexIdsEnabled = true;
      identityIdsEnabled = true;
    }

    // View list/item/index stats permission
    if (value.action === 'view' && value.resourceType === 'stats') {
      listIdsEnabled = true;
      itemIdsEnabled = true;
      indexIdsEnabled = true;
      identityIdsEnabled = true;
    }

    // Run flow permissions
    if (
      ['run', 'run-with-identity'].includes(value.action) &&
      value.resourceType === 'flow'
    ) {
      flowIdsEnabled = true;
    }

    setResourceTypeEnabled(resourceTypeEnabled);
    setListIdsEnabled(listIdsEnabled);
    setItemIdsEnabled(itemIdsEnabled);
    setIndexIdsEnabled(indexIdsEnabled);
    setIdentityIdsEnabled(identityIdsEnabled);
    setGroupsEnabled(groupsEnabled);
    setFlowIdsEnabled(flowIdsEnabled);
  }, [value]);

  const handleChangeMode = useCallback(
    (
      selected: SingleValue<
        SelectOptionProps<{
          mode: PermissionMode;
        }>
      >
    ) => {
      if (!selected) {
        return;
      }

      const newValue = {
        ...value,
        mode: selected.value.mode,
      };

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const handleChangeAction = useCallback(
    (
      selected: SingleValue<
        SelectOptionProps<{
          action: PermissionAction;
        }>
      >
    ) => {
      if (!selected) {
        return;
      }

      const newValue: Permission = {
        ...value,
        action: selected.value.action,
      };

      // A sync schema permission is only a mode and an action
      if (newValue.action === 'sync-schema') {
        delete newValue.resourceType;
        delete newValue.listIds;
        delete newValue.itemIds;
        delete newValue.indexIds;
        delete newValue.identityIds;
        delete newValue.groups;
        delete newValue.flowIds;
      }

      // Flows can only be run, and running only applies to flows
      const runs = ['run', 'run-with-identity'].includes(newValue.action);
      if (runs && newValue.resourceType !== 'flow') {
        newValue.resourceType = 'flow';
        delete newValue.listIds;
        delete newValue.itemIds;
        delete newValue.indexIds;
        delete newValue.identityIds;
        delete newValue.groups;
      }
      if (!runs && newValue.resourceType === 'flow') {
        delete newValue.resourceType;
        delete newValue.flowIds;
      }

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const handleChangeResourceType = useCallback(
    (
      selected: SingleValue<
        SelectOptionProps<{
          resourceType: PermissionResourceType | null;
        }>
      >
    ) => {
      if (!selected) {
        return;
      }

      const newValue = {
        ...value,
        resourceType: selected.value.resourceType ?? undefined,
      };

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const handleSelectLists = useCallback(
    (selected: string[]) => {
      const newValue = {
        ...value,
        listIds:
          selected && Array.isArray(selected) && selected.length > 0
            ? selected
            : undefined,
      };

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const handleSelectItems = useCallback(
    (selected: string[]) => {
      const newValue = {
        ...value,
        itemIds:
          selected && Array.isArray(selected) && selected.length > 0
            ? selected
            : undefined,
      };

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const handleSelectIndexes = useCallback(
    (selected: string[]) => {
      const newValue = {
        ...value,
        indexIds:
          selected && Array.isArray(selected) && selected.length > 0
            ? selected
            : undefined,
      };

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const handleSelectIdentities = useCallback(
    (selected: string[]) => {
      const newValue = {
        ...value,
        identityIds:
          selected && Array.isArray(selected) && selected.length > 0
            ? selected
            : undefined,
      };

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const handleSelectFlows = useCallback(
    (selected: string[]) => {
      const newValue = {
        ...value,
        flowIds: selected.length > 0 ? selected : undefined,
      };

      setValue(newValue);
      onChange?.(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const permissionModeOptions: SelectOptionProps<{
    mode: PermissionMode;
  }>[] = [
    {
      label: 'Allow',
      icon: faCircleCheck as IconDefinition,
      value: {
        mode: 'allow',
      },
    },
    {
      label: 'Block',
      icon: faBan,
      value: {
        mode: 'block',
      },
    },
  ];

  const permissionActionOptions: SelectOptionProps<{
    action: PermissionAction;
  }>[] = [
    {
      label: 'All',
      icon: faAsterisk,
      value: {
        action: '*',
      },
    },
    {
      label: 'Create',
      icon: faSquarePlus,
      value: {
        action: 'create',
      },
    },
    {
      label: 'View',
      icon: faEye,
      value: {
        action: 'view',
      },
    },
    {
      label: 'Update',
      icon: faEdit,
      value: {
        action: 'update',
      },
    },
    {
      label: 'Delete',
      icon: faTrash,
      value: {
        action: 'delete',
      },
    },
    {
      label: 'Restore',
      icon: faClockRotateLeft,
      value: {
        action: 'restore',
      },
    },
    {
      label: 'Register',
      icon: faIdCard,
      value: {
        action: 'register',
      },
    },
    {
      label: 'Authenticate',
      icon: faPassport,
      value: {
        action: 'authenticate',
      },
    },
    {
      label: 'Reset password',
      icon: faUserLock,
      value: {
        action: 'reset-password',
      },
    },
    {
      label: 'Verify email',
      icon: faEnvelopeCircleCheck,
      value: {
        action: 'verify-email',
      },
    },
    {
      label: 'Create using identity',
      icon: faSquarePlus,
      value: {
        action: 'create-with-identity',
      },
    },
    {
      label: 'View using identity',
      icon: faEye,
      value: {
        action: 'view-with-identity',
      },
    },
    {
      label: 'Update using identity',
      icon: faEdit,
      value: {
        action: 'update-with-identity',
      },
    },
    {
      label: 'Delete using identity',
      icon: faTrash,
      value: {
        action: 'delete-with-identity',
      },
    },
    {
      label: 'Restore using identity',
      icon: faClockRotateLeft,
      value: {
        action: 'restore-with-identity',
      },
    },
    {
      label: 'Sync schema',
      icon: faSitemap,
      value: {
        action: 'sync-schema',
      },
    },
    {
      label: 'Run',
      icon: faPlay,
      value: {
        action: 'run',
      },
    },
    {
      label: 'Run using identity',
      icon: faPlay,
      value: {
        action: 'run-with-identity',
      },
    },
  ];

  const getPermissionResourceTypeOptions = useCallback((): SelectOptionProps<{
    resourceType: PermissionResourceType | null;
  }>[] => {
    let listOptionEnabled = false;
    let itemOptionEnabled = false;
    let indexOptionEnabled = false;
    let identityOptionEnabled = false;
    let eventOptionEnabled = false;
    let statsOptionEnabled = false;
    let flowOptionEnabled = false;

    if (['run', 'run-with-identity'].includes(value.action)) {
      flowOptionEnabled = true;
    }

    if (value.action === 'create') {
      listOptionEnabled = true;
      itemOptionEnabled = true;
      indexOptionEnabled = true;
      identityOptionEnabled = true;
    }

    if (value.action === 'view') {
      listOptionEnabled = true;
      itemOptionEnabled = true;
      indexOptionEnabled = true;
      identityOptionEnabled = true;
      eventOptionEnabled = true;
      statsOptionEnabled = true;
    }

    if (['update', 'delete'].includes(value.action)) {
      listOptionEnabled = true;
      itemOptionEnabled = true;
      indexOptionEnabled = true;
      identityOptionEnabled = true;
    }

    if (value.action === 'restore') {
      itemOptionEnabled = true;
    }

    if (
      [
        'create-with-identity',
        'view-with-identity',
        'update-with-identity',
        'delete-with-identity',
        'restore-with-identity',
      ].includes(value.action)
    ) {
      itemOptionEnabled = true;
    }

    if (value.action === 'view-with-identity') {
      eventOptionEnabled = true;
      statsOptionEnabled = true;
    }

    if (
      ['register', 'authenticate', 'reset-password', 'verify-email'].includes(
        value.action
      )
    ) {
      identityOptionEnabled = true;
    }

    return [
      {
        label: 'Clear',
        icon: faTimes,
        value: {
          resourceType: null,
        },
      },
      {
        label: 'List',
        icon: faFolder,
        value: {
          resourceType: 'list',
        },
        disabled: !listOptionEnabled,
      },
      {
        label: 'Item',
        icon: faFileCode,
        value: {
          resourceType: 'item',
        },
        disabled: !itemOptionEnabled,
      },
      {
        label: 'Index',
        icon: faTags,
        value: {
          resourceType: 'index',
        },
        disabled: !indexOptionEnabled,
      },
      {
        label: 'Identity',
        icon: faPassport,
        value: {
          resourceType: 'identity',
        },
        disabled: !identityOptionEnabled,
      },
      {
        label: 'Events',
        icon: faChartSimple,
        value: {
          resourceType: 'event',
        },
        disabled: !eventOptionEnabled,
      },
      {
        label: 'Stats',
        icon: faChartLine,
        value: {
          resourceType: 'stats',
        },
        disabled: !statsOptionEnabled,
      },
      {
        label: 'Flow',
        icon: faDiagramProject,
        value: {
          resourceType: 'flow',
        },
        disabled: !flowOptionEnabled,
      },
    ];
  }, [value.action]);

  return (
    <Card className="token-permission-editor" background={false}>
      <h2>
        <span className="extra">#{index + 1}</span>
      </h2>
      <ValueList fullWidth>
        <Value
          label="Mode"
          htmlFor={`permissions-${index}-mode`}
          value={
            <Select
              id={`permissions-${index}-mode`}
              name={`permissions-${index}-mode`}
              options={permissionModeOptions}
              value={permissionModeOptions.find(
                option => option.value.mode === value.mode
              )}
              disabled={pending}
              onChange={handleChangeMode as any}
            />
          }
        />
        <Value
          label="Action"
          htmlFor={`permissions-${index}-action`}
          value={
            <>
              <Select
                id={`permissions-${index}-action`}
                name={`permissions-${index}-action`}
                options={permissionActionOptions}
                value={permissionActionOptions.find(
                  option => option.value.action === value.action
                )}
                disabled={pending}
                onChange={handleChangeAction as any}
              />
              {(value.action === 'update-with-identity' ||
                value.action === 'delete-with-identity') && (
                <span className="extra">
                  An identity can normally only write items it owns. A list
                  whose write rules declare <code>shared</code> lets it write
                  items it doesn't own, and the rules decide who can.
                </span>
              )}
            </>
          }
        />
        {(options => (
          <Value
            label="Resource type"
            htmlFor={`permissions-${index}-resourceType`}
            value={
              <Select
                id={`permissions-${index}-resourceType`}
                name={`permissions-${index}-resourceType`}
                options={options}
                value={
                  options.find(
                    option => option.value.resourceType === value.resourceType
                  ) ?? null
                }
                disabled={pending || !resourceTypeEnabled}
                onChange={handleChangeResourceType as any}
              />
            }
          />
        ))(getPermissionResourceTypeOptions())}
        <Value
          label="Lists"
          htmlFor={`permissions-${index}-listIds`}
          value={
            <ResourcePicker
              resourceType="list"
              name={`permissions-${index}-listIds`}
              value={permission.listIds}
              onChange={handleSelectLists}
              disabled={pending || !listIdsEnabled}
            />
          }
        />
        <Value
          label="Items"
          htmlFor={`permissions-${index}-itemIds`}
          value={
            <ResourcePicker
              resourceType="item"
              name={`permissions-${index}-itemIds`}
              value={permission.itemIds}
              onChange={handleSelectItems}
              disabled={pending || !itemIdsEnabled}
            />
          }
        />
        <Value
          label="Indexes"
          htmlFor={`permissions-${index}-indexIds`}
          value={
            <ResourcePicker
              resourceType="index"
              name={`permissions-${index}-indexIds`}
              value={permission.indexIds}
              onChange={handleSelectIndexes}
              disabled={pending || !indexIdsEnabled}
            />
          }
        />
        <Value
          label="Identities"
          htmlFor={`permissions-${index}-identityIds`}
          value={
            <ResourcePicker
              resourceType="identity"
              name={`permissions-${index}-identityIds`}
              value={permission.identityIds}
              onChange={handleSelectIdentities}
              disabled={pending || !identityIdsEnabled}
            />
          }
        />
        <Value
          label="Groups"
          htmlFor={`permissions-${index}-groups`}
          value={
            <ArrayInput
              values={permission.groups}
              onChange={groups => {
                const newValue = {
                  ...value,
                  groups,
                };

                setValue(newValue);
                onChange?.(newValue);
              }}
              disabled={pending || !groupsEnabled}
            />
          }
        />
        <Value
          label="Flows"
          htmlFor={`permissions-${index}-flowIds`}
          value={
            <FlowIdsPicker
              name={`permissions-${index}-flowIds`}
              value={value.flowIds}
              onChange={handleSelectFlows}
              disabled={pending || !flowIdsEnabled}
            />
          }
        />
      </ValueList>
      <Alert
        intent="warning"
        show={value.mode === 'allow' && value.action === '*'}
      >
        <p>
          This permission allows everything, including issuing password reset
          tokens. Anyone holding this token could take over identities, so never
          use it in a browser or app that other people can inspect.
        </p>
      </Alert>
      <Alert
        intent="warning"
        show={
          value.mode === 'allow' &&
          ['reset-password', 'verify-email'].includes(value.action)
        }
      >
        <p>
          Unless these groups use webhook token delivery, anyone holding this
          token can be given reset or verification tokens, and could take over
          any identity in them. Only use it in a token that never leaves your
          server.
        </p>
      </Alert>
    </Card>
  );
}
